import { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, Loader2, Camera } from "lucide-react";

function compressPhoto(f) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(f);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const max = 400;
      let { width, height } = img;
      const scale = max / Math.max(width, height);
      if (scale < 1) { width = Math.round(width * scale); height = Math.round(height * scale); }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", 0.8));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("imagem inválida")); };
    img.src = url;
  });
}

export default function PatientEditDialog({ patient, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();
  const [f, setF] = useState({
    name: patient.name || "", sex: patient.sex || "", age: patient.age ?? "",
    birth_date: patient.birth_date || "", cpf: patient.cpf || "", phone: patient.phone || "",
    email: patient.email || "", insurance: patient.insurance || "", history: patient.history || "",
    photo: patient.photo || "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const onPhoto = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) { toast.error("Use JPG, PNG ou WEBP"); return; }
    try { setF((p) => ({ ...p, photo: "" })); const data = await compressPhoto(file); setF((p) => ({ ...p, photo: data })); }
    catch { toast.error("Não foi possível processar a foto"); }
  };

  const save = async () => {
    if (!f.name.trim()) { toast.error("Informe o nome"); return; }
    setSaving(true);
    try {
      await api.put(`/patients/${patient.id}`, {
        name: f.name, sex: f.sex || null, age: f.age === "" ? null : parseInt(f.age),
        birth_date: f.birth_date || null, cpf: f.cpf || null, phone: f.phone || null,
        email: f.email || null, insurance: f.insurance || null, history: f.history || null,
        photo: f.photo || null,
      });
      toast.success("Ficha atualizada");
      setOpen(false);
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="edit-patient-btn"><Pencil className="w-4 h-4 mr-2" /> Ficha de paciente</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="patient-edit-dialog">
        <DialogHeader>
          <DialogTitle>Ficha de paciente</DialogTitle>
          <DialogDescription>Dados cadastrais, foto e convênio.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center shrink-0 border border-border">
              {f.photo ? <img src={f.photo} alt="foto" className="w-full h-full object-cover" data-testid="patient-photo-preview" />
                : <span className="font-heading font-bold text-primary text-xl">{(f.name || "P").slice(0, 2).toUpperCase()}</span>}
            </div>
            <div>
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="patient-photo-btn">
                <Camera className="w-4 h-4 mr-2" /> {f.photo ? "Trocar foto" : "Adicionar foto"}
              </Button>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden
                onChange={(e) => onPhoto(e.target.files[0])} data-testid="patient-photo-input" />
              <p className="text-xs text-muted-foreground mt-2">JPG, PNG ou WEBP</p>
            </div>
          </div>

          <div className="space-y-2"><Label>Nome completo</Label>
            <Input value={f.name} onChange={set("name")} data-testid="patient-name-input" /></div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2"><Label>Sexo</Label>
              <Select value={f.sex} onValueChange={(v) => setF({ ...f, sex: v })}>
                <SelectTrigger data-testid="patient-sex-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Feminino">Feminino</SelectItem>
                  <SelectItem value="Masculino">Masculino</SelectItem>
                  <SelectItem value="Outro">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Idade</Label>
              <Input type="number" value={f.age} onChange={set("age")} data-testid="patient-age-input" /></div>
            <div className="space-y-2"><Label>Nascimento</Label>
              <Input type="date" value={f.birth_date} onChange={set("birth_date")} data-testid="patient-birth-input" /></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>CPF</Label>
              <Input value={f.cpf} onChange={set("cpf")} placeholder="000.000.000-00" data-testid="patient-cpf-input" /></div>
            <div className="space-y-2"><Label>Telefone</Label>
              <Input value={f.phone} onChange={set("phone")} placeholder="(00) 00000-0000" data-testid="patient-phone-input" /></div>
            <div className="space-y-2"><Label>E-mail</Label>
              <Input value={f.email} onChange={set("email")} data-testid="patient-email-input" /></div>
            <div className="space-y-2"><Label>Convênio</Label>
              <Input value={f.insurance} onChange={set("insurance")} placeholder="Ex: Unimed" data-testid="patient-insurance-input" /></div>
          </div>

          <div className="space-y-2"><Label>Histórico / observações</Label>
            <Textarea rows={2} value={f.history} onChange={set("history")} data-testid="patient-history-input" /></div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="save-patient-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Salvar ficha
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
